import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2, Caption1 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'
import { MT4Icon } from '@ui/icons/platforms/MT4.icon'
import { MT5Icon } from '@ui/icons/platforms/MT5.icon'
import { MobileAppIcon } from '@ui/icons/platforms/MobileApp.icon'
import { TradingViewIcon } from '@ui/icons/platforms/TradingView.icon'
import { TableNoResults } from '@ui/v2/Table/Table'

import { PageQuery } from '@utils/ApiClient'

import { PlatformType } from '@model/PlatformTypeDto'
import { PlatformTypeEnum, getDisplayPlatformName } from '@model/PlatformTypeEnum'
import { TradingAccount, isTradingAccountStatusReadOnly } from '@model/TradingAccount'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import styles from './InactiveTradingAccountsCards.module.scss'

export interface InactiveLiveTradingAccountsCardsProps {
  tableQuery: { data: TradingAccount[]; isLoading: boolean }
  pageQuery?: PageQuery
  setPageQuery?: (q: PageQuery) => void
  platformTypes?: PlatformType[]
}

const getPlatformLogo = (platformTypeId: PlatformTypeEnum) => {
  switch (platformTypeId) {
    case PlatformTypeEnum.MT4:
      return <MT4Icon size={40} />
    case PlatformTypeEnum.MT5:
      return <MT5Icon size={40} />
    case PlatformTypeEnum.TickmillTrader:
      return <MobileAppIcon size={40} />
    default:
      return <TradingViewIcon size={40} />
  }
}

interface AccountHeaderProps {
  tradingAccount: TradingAccount
  balance: string
}

const AccountHeader: FC<AccountHeaderProps> = ({ tradingAccount, balance }) => {
  const { t } = useTranslation()

  return (
    <Box className={styles.balanceWrapper}>
      <Box className={styles.logoCircle}>{getPlatformLogo(tradingAccount.platformType.id)}</Box>
      <Box className={styles.balance}>
        <Caption1 className={styles.accountName} isParagraph>
          {tradingAccount.nickname || tradingAccount.name}
        </Caption1>
        <Body1 className={styles.accountBalance} semibold>
          <SensitiveData>{balance}</SensitiveData>
        </Body1>
      </Box>
      {isTradingAccountStatusReadOnly(tradingAccount.status) ? (
        <Badge type='yellow' size='xxs' contrast='low'>
          {t('Trading Account.Read Only')}
        </Badge>
      ) : (
        <Badge type='yellow' size='xxs'>
          {t('Deactivated')}
        </Badge>
      )}
    </Box>
  )
}

interface AccountDetailsProps {
  tradingAccount: TradingAccount
}

const AccountDetails: FC<AccountDetailsProps> = ({ tradingAccount }) => {
  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()
  const equity = formatMoney(tradingAccount.platformOverview.equity, tradingAccount.currency.id)

  return (
    <Box className={styles.accountDetails}>
      <Box className={styles.detailItem}>
        <Caption1 isParagraph className={styles.inactiveText}>
          {t('Trading Account.Account Type')}
        </Caption1>
        <Body2 isParagraph className={styles.inactiveText}>
          {tradingAccount.accountType.name}
        </Body2>
      </Box>
      <Box className={styles.detailItem}>
        <Caption1 isParagraph className={styles.inactiveText}>
          {t('Trading Account.Leverage')}
        </Caption1>
        <Body2 isParagraph className={styles.inactiveText}>
          {tradingAccount.leverageType?.name}
        </Body2>
      </Box>
      <Box className={styles.detailItem}>
        <Caption1 isParagraph className={styles.inactiveText}>
          {t('Trading Account.Equity')}
        </Caption1>
        <Body2 isParagraph className={styles.inactiveText}>
          {equity}
        </Body2>
      </Box>
      <Box className={styles.detailItem}>
        <Caption1 isParagraph className={styles.inactiveText}>
          {t('Trading Account.Platform')}
        </Caption1>
        <Body2 className={styles.inactiveText}>
          {getDisplayPlatformName(tradingAccount.platformType)}
        </Body2>
      </Box>
    </Box>
  )
}

interface InactiveTradingAccountCardProps {
  tradingAccount: TradingAccount
  formatMoney: (amount: number, currency: string) => string
}

const InactiveTradingAccountCard: FC<InactiveTradingAccountCardProps> = ({
  tradingAccount,
  formatMoney,
}) => {
  const computedData = useMemo(() => {
    const balance = formatMoney(
      (tradingAccount as any)?.platformOverview?.balance ?? (tradingAccount as any)?.balance ?? 0,
      tradingAccount.currency.id
    )

    return {
      balance,
    }
  }, [tradingAccount, formatMoney])

  return (
    <Box className={styles.cardWrapper} key={tradingAccount.id}>
      <Box className={styles.card} gap='x16'>
        <Box className={styles.title}>
          <AccountHeader tradingAccount={tradingAccount} balance={computedData.balance} />
        </Box>
        <div className={styles.divider}></div>
        <AccountDetails tradingAccount={tradingAccount} />
      </Box>
    </Box>
  )
}

export const InactiveLiveTradingAccountsCards: React.FC<InactiveLiveTradingAccountsCardsProps> = (
  props
) => {
  const { formatMoney } = useFormatNumber()

  const {
    tableQuery: { data },
  } = props

  return (
    <div className={styles.cards}>
      {!data.length ? (
        <TableNoResults />
      ) : (
        data.map((tradingAccount) => (
          <React.Fragment key={tradingAccount.id}>
            <InactiveTradingAccountCard tradingAccount={tradingAccount} formatMoney={formatMoney} />
          </React.Fragment>
        ))
      )}
    </div>
  )
}
